/**
 * AudioRecorder Component
 *
 * Handles audio recording for consultations with real-time streaming via LiveKit:
 * - Record audio using LiveKit's audio capture
 * - Stream audio chunks via LiveKit WebRTC
 * - Display recording status and duration
 * - Visual audio level indicator
 * - Start/stop/pause controls
 * - Submit for analysis
 */

import { useState, useRef, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import { RotateCcw } from 'lucide-react';
import { Mic, Play, SaveDraft } from '@/assets/icons';
import { Button } from '@/components/base';
import { useIsAyurveda } from '@/ayurveda';
import WaveSurfer from 'wavesurfer.js';
import RecordPlugin from 'wavesurfer.js/dist/plugins/record.js';

import {
  type StreamingMessage,
  type StreamingCDSSData,
  formatDuration,
  type AIAnalysisResult,
  type SessionInfoResponse,
} from '@/lib/types/consultation';
import { toast } from 'sonner';
import { createComponentLogger } from '@/lib/utils/logger';
import { useLiveKitConsultation } from '@/lib/hooks/useLiveKitConsultation';
import type {
  CDSSData,
  SessionInfoResponse as LiveKitSessionInfoResponse,
  LiveKitClient,
} from '@/lib/livekit/LiveKitClient';
import { useBackgroundNoiseDetection } from '@/lib/hooks/useBackgroundNoiseDetection';

const logger = createComponentLogger('AudioRecorder');

/** Exact Figma "Pause" icon for the On Hold button. */
function OnHoldIcon({ color }: { color: string }) {
  return (
    <svg width="9" height="10.5" viewBox="0 0 9 10.5" fill="none" aria-hidden="true">
      <path d="M6 10.5V0H9V10.5H6ZM0 10.5V0H3V10.5H0Z" fill={color} />
    </svg>
  );
}

/** Exact Figma "Inbox" icon for the Save record button. */
function SaveRecordIcon() {
  return (
    <svg width="13.5" height="13.5" viewBox="0 0 13.5 13.5" fill="none" aria-hidden="true">
      <path
        d="M1.5 13.5C1.0875 13.5 0.73125 13.3563 0.43125 13.0688C0.14375 12.7688 0 12.4125 0 12V1.5C0 1.0875 0.14375 0.737501 0.43125 0.450001C0.73125 0.15 1.0875 0 1.5 0H12C12.4125 0 12.7625 0.15 13.05 0.450001C13.35 0.737501 13.5 1.0875 13.5 1.5V12C13.5 12.4125 13.35 12.7688 13.05 13.0688C12.7625 13.3563 12.4125 13.5 12 13.5H1.5ZM6.75 9.75C7.225 9.75 7.65625 9.6125 8.04375 9.3375C8.43125 9.0625 8.7 8.7 8.85 8.25H12V1.5H1.5V8.25H4.65C4.8 8.7 5.06875 9.0625 5.45625 9.3375C5.84375 9.6125 6.275 9.75 6.75 9.75Z"
        fill="#F8F9FB"
      />
    </svg>
  );
}

const TOAST_IDS = {
  internetConnectionLost: 'internetConnectionLost',
  internetReconnecting: 'internetReconnecting',
  slowInternet: 'slowInternet',
  backgroundNoise: 'backgroundNoise',
  processingError: 'processingError',
} as const;

/**
 * Props for AudioRecorder component
 */
export interface AudioRecorderProps {
  /** Consultation ID */
  consultationId: string;
  /** Patient ID for auto-saving DiagnosticReport (optional) */
  patientId?: string;
  /** Auth token for WebSocket connections */
  authToken: string;
  /** Callback when transcription received */
  onTranscription?: (message: StreamingMessage) => void;
  /** Callback when CDSS suggestion received */
  onCDSSSuggestion?: (message: StreamingMessage) => void;
  /** Callback when CDSS data received */
  onCDSSData?: (data: StreamingCDSSData) => void;
  /** Callback when recording state changes */
  onRecordingStateChange?: (isRecording: boolean, isPaused: boolean) => void;
  /** Callback when a Submit/Save Draft round-trip (stop → send → await
   *  agent summary) is in flight. `isRecording`/`isPaused` both go false as
   *  soon as this starts (recording is stopped immediately), even though
   *  there's still unsaved work in flight — callers that need to know
   *  "is there anything that could still be lost" should also watch this. */
  onSubmittingChange?: (isSubmitting: boolean) => void;
  /** Callback when analysis is complete */
  onAnalysisComplete?: (analysis: AIAnalysisResult) => void;
  /** Callback when session summary is received from LiveKit */
  onSessionSummary?: (summary: SessionInfoResponse) => void;
  /** Disable recording */
  disabled?: boolean;
  /** Auto-start recording when component mounts */
  autoStart?: boolean;
  /** Whether this is a continuation of a previous session */
  isContinuation?: boolean;
  /** JSON-stringified previous CDSS summary for continuation sessions */
  previousSummary?: string;
  /** Pre-fetched vitals data to send to agent */
  vitals?: unknown;
  /** Pre-fetched SOAP note data to send to agent */
  soapData?: unknown;
  /** Callback to save current CDSS data as draft and navigate to dashboard */
  onSaveDraft?: (summary: SessionInfoResponse) => void;
  /** Whether draft save is in progress */
  isSavingDraft?: boolean;
  /** Callback fired immediately when Save Record is clicked (before LiveKit
   *  submission). Pass `true` for Quick Submit so the saved record is marked
   *  accordingly (there's otherwise no way to tell the two apart later). */
  onSaveRecord?: (isQuickSubmit?: boolean) => void;
  /** Callback fired once Quick Submit's session_quick_end signal has been sent —
   *  caller should navigate away immediately (e.g. to the dashboard); no summary
   *  will arrive on this path, it's written directly to Medplum in the background. */
  onQuickSubmit?: () => void;
  /** Fired once the underlying LiveKitClient instance is available, so callers
   *  can send outbound control messages (e.g. clinical assessment overrides)
   *  without needing to lift useLiveKitConsultation up themselves. */
  onLiveKitClientReady?: (client: LiveKitClient) => void;
}

/**
 * Imperative handle exposed by AudioRecorder so callers (e.g. a
 * "leave this consultation?" confirmation) can trigger the same Save Draft
 * flow as the on-screen button without lifting all of its internal state.
 */
export interface AudioRecorderHandle {
  saveDraft: () => void;
  /** Snapshot of the current recording duration, in seconds. */
  getDurationSeconds: () => number;
}

/**
 * AudioRecorder Component
 */
export const AudioRecorder = forwardRef<AudioRecorderHandle, AudioRecorderProps>(
  function AudioRecorder(
    {
      consultationId,
      patientId,
      authToken,
      onTranscription,
      onCDSSSuggestion,
      onCDSSData,
      onRecordingStateChange,
      onSubmittingChange,
      onAnalysisComplete,
      onSessionSummary,
      disabled = false,
      autoStart = false,
      isContinuation = false,
      previousSummary,
      vitals,
      soapData,
      onSaveDraft,
      isSavingDraft = false,
      onSaveRecord,
      onQuickSubmit,
      onLiveKitClientReady,
    },
    ref
  ) {
    // Ayurveda gets the earthy olive/green palette; every other department
    // keeps the original blue palette this component used before the
    // Ayurveda redesign — recolor only, layout/icons stay the same either way.
    const isAyurveda = useIsAyurveda();
    const accentColor = isAyurveda ? '#8AA165' : '#061A61';
    const accentHoverBgClass = isAyurveda ? 'hover:bg-[#799457]' : 'hover:bg-[#0a2580]';
    const accentBgClass = isAyurveda ? 'bg-[#8AA165]' : 'bg-[#061a61]';
    const accentTextClass = isAyurveda ? 'text-[#8AA165]' : 'text-[#061A61]';
    const micActiveColor = isAyurveda ? '#8AA165' : '#243056';
    const micInactiveColor = isAyurveda ? '#A3B28B' : '#678AF6';
    const waveColor = isAyurveda ? '#8AA165' : '#678AF6';
    const waveProgressColor = isAyurveda ? '#5C6B3F' : '#061a61';
    const gradientBarClass = isAyurveda
      ? 'rounded-[16px] border border-solid border-[#fefeff] bg-gradient-to-r from-[rgba(173,173,97,0.2)] to-[rgba(102,102,49,0.2)] p-[12px] shadow-[0px_2px_36px_20px_rgba(176,173,170,0.15)] backdrop-blur-[15px]'
      : 'rounded-[16px] border border-solid border-[#fefeff] bg-gradient-to-r from-[rgba(103,138,246,0.2)] to-[rgba(6,26,97,0.2)] p-[12px] shadow-[0px_2px_36px_20px_rgba(176,173,170,0.15)] backdrop-blur-[15px]';

    // Recording state
    const [isRecording, setIsRecording] = useState(false);
    const [isPaused, setIsPaused] = useState(false);
    const [duration, setDuration] = useState(0);
    const [hasRecording, setHasRecording] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    // Initialize to true when autoStart is enabled - shows "Connecting..." immediately
    const [isConnectingToAgent, setIsConnectingToAgent] = useState(autoStart && !disabled);
    // Track if submission has been initiated - prevents resume/re-submit
    const [hasSubmitted, setHasSubmitted] = useState(false);

    // Report every isSubmitting transition — simpler and more reliable than
    // threading a callback through each of the several places that set it.
    useEffect(() => {
      onSubmittingChange?.(isSubmitting);
    }, [isSubmitting, onSubmittingChange]);

    // Background noise detection — active only while mic is recording
    const { isHighNoise } = useBackgroundNoiseDetection(isRecording && !isPaused);

    // Ref to track agent ready promise resolver
    const agentReadyResolverRef = useRef<(() => void) | null>(null);

    // Ref to track component mount status - prevents state updates after unmount
    const isMountedRef = useRef(false);

    // Set isMountedRef to true on mount, false on unmount
    useEffect(() => {
      isMountedRef.current = true;
      return () => {
        isMountedRef.current = false;
      };
    }, []);

    // Ref to track the session summary timeout - cleared when summary is received
    const summaryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    // Ref for the initial "please wait" warning (fires at 20s)
    const waitWarningTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    // Tracks whether the in-flight submission was triggered by Save Draft (vs Submit),
    // so onSessionSummary/error/timeout paths know how to route the result.
    const isDraftSubmissionRef = useRef(false);

    // LiveKit integration
    const livekit = useLiveKitConsultation({
      consultationId,
      patientId,
      authToken,
      isContinuation,
      previousSummary,
      vitals,
      soapData,
      onReady: () => {
        logger.log('onReady callback fired - agent is ready');
        // Resolve the promise if we're waiting for agent ready
        if (agentReadyResolverRef.current) {
          agentReadyResolverRef.current();
          agentReadyResolverRef.current = null;
        }
      },
      onTranscription: (data) => {
        // Map LiveKit transcription to WebSocket format for compatibility
        onTranscription?.({
          type: 'transcription',
          text: data.text,
          context_state: undefined,
          speaker: data.speaker,
        } as StreamingMessage);
      },
      onCDSS: (data: CDSSData) => {
        // Map LiveKit CDSS data to WebSocket format for compatibility
        onCDSSSuggestion?.({
          type: 'suggestion',
          text: data.possible_diagnoses?.join(', ') || '',
          context_state: undefined,
          speaker: undefined,
        } as StreamingMessage);
        onCDSSData?.(data);
      },
      onAnalysisComplete: (data) => {
        // Map LiveKit analysis complete to WebSocket format
        onAnalysisComplete?.(data.result as unknown as AIAnalysisResult);
      },
      onSessionSummary: (data: LiveKitSessionInfoResponse) => {
        // Handle session summary from LiveKit agent
        logger.log('Session summary received in AudioRecorder:', data);

        // Clear all timeouts since we received a response
        if (summaryTimeoutRef.current) {
          clearTimeout(summaryTimeoutRef.current);
          summaryTimeoutRef.current = null;
        }
        if (waitWarningTimeoutRef.current) {
          clearTimeout(waitWarningTimeoutRef.current);
          waitWarningTimeoutRef.current = null;
        }

        setIsSubmitting(false);
        setHasRecording(false);

        // Determine which button triggered this submission before clearing the ref,
        // so a subsequent submission never inherits a stale draft flag.
        const wasDraft = isDraftSubmissionRef.current;
        isDraftSubmissionRef.current = false;

        // Map to SessionInfoResponse type and call callback
        if (data.success && data.summary) {
          const sessionInfo: SessionInfoResponse = {
            success: data.success,
            summary: data.summary as unknown as SessionInfoResponse['summary'],
            summary_file: data.summary_file,
          };
          if (wasDraft) {
            onSaveDraft?.(sessionInfo);
          } else {
            onSessionSummary?.(sessionInfo);
            toast.success('Summary received!', { duration: 2000 });
          }
        } else {
          if (wasDraft) {
            toast.error('Failed to save draft', { duration: 3000 });
          } else {
            toast.error('Failed to generate summary', { duration: 3000 });
          }
        }
      },
      onError: (error) => {
        logger.error('LiveKit error:', error);
        toast.error(`LiveKit Error: ${error.message}`, { duration: 3000 });
        if (summaryTimeoutRef.current) {
          clearTimeout(summaryTimeoutRef.current);
          summaryTimeoutRef.current = null;
        }
        if (waitWarningTimeoutRef.current) {
          clearTimeout(waitWarningTimeoutRef.current);
          waitWarningTimeoutRef.current = null;
        }
        setIsSubmitting(false);
        setHasSubmitted(false);
        isDraftSubmissionRef.current = false;
        livekit.resetForRetry();
      },
      showToastNotifications: false, // We'll handle toast notifications here
    });

    // Notify the caller once the underlying LiveKitClient instance is available,
    // so it can send outbound control messages (e.g. clinical assessment
    // overrides) without needing to lift useLiveKitConsultation up itself.
    useEffect(() => {
      if (livekit.client) {
        onLiveKitClientReady?.(livekit.client);
      }
    }, [livekit.client, onLiveKitClientReady]);

    // Sync duration from LiveKit hook
    useEffect(() => {
      if (livekit.isRecording) {
        setDuration(livekit.duration);
      }
    }, [livekit.isRecording, livekit.duration]);

    // Backend processing failures (e.g. "No audio was recorded") arrive as a
    // processing_status update, separate from the onError handler above — show
    // a toast for these too, since showToastNotifications is off for this hook
    // instance (this component owns its own toast notifications).
    useEffect(() => {
      if (livekit.processingStatus === 'error') {
        toast.error(livekit.processingErrorMessage || 'Processing failed', {
          id: TOAST_IDS.processingError,
          duration: 5000,
        });
      } else {
        toast.dismiss(TOAST_IDS.processingError);
      }
    }, [livekit.processingStatus, livekit.processingErrorMessage]);

    // Network status toasts — shown while recording, dismissed when condition clears.
    // Stable toast IDs prevent stacking on repeated state changes.
    useEffect(() => {
      const dismissNetworkToasts = () => {
        toast.dismiss(TOAST_IDS.internetConnectionLost);
        toast.dismiss(TOAST_IDS.internetReconnecting);
        toast.dismiss(TOAST_IDS.slowInternet);
      };

      if (!isRecording) {
        dismissNetworkToasts();
        return;
      }

      const state = livekit.connectionState;
      if (state === 'disconnected') {
        toast.dismiss(TOAST_IDS.internetReconnecting);
        toast.dismiss(TOAST_IDS.slowInternet);
        toast.error('Internet connection lost \u2014 audio recording is interrupted.', {
          id: TOAST_IDS.internetConnectionLost,
          duration: Infinity,
        });
      } else if (state === 'reconnecting') {
        toast.dismiss(TOAST_IDS.internetConnectionLost);
        toast.dismiss(TOAST_IDS.slowInternet);
        toast.warning(
          'Reconnecting\u2026 audio recording is paused until the connection restores.',
          {
            id: TOAST_IDS.internetReconnecting,
            duration: Infinity,
          }
        );
      } else {
        // connected
        toast.dismiss(TOAST_IDS.internetConnectionLost);
        toast.dismiss(TOAST_IDS.internetReconnecting);
        if (livekit.connectionQuality === 'poor') {
          toast.warning('Slow internet detected \u2014 transcription quality may be affected.', {
            id: TOAST_IDS.slowInternet,
            duration: Infinity,
          });
        } else {
          toast.dismiss(TOAST_IDS.slowInternet);
        }
      }

      return dismissNetworkToasts;
    }, [isRecording, livekit.connectionState, livekit.connectionQuality]);

    // Background noise toast
    useEffect(() => {
      if (!isRecording || !isHighNoise) {
        toast.dismiss(TOAST_IDS.backgroundNoise);
        return;
      }
      toast.warning(
        'High background noise detected \u2014 move to a quieter environment for better accuracy.',
        { id: TOAST_IDS.backgroundNoise, duration: Infinity }
      );

      return () => {
        toast.dismiss(TOAST_IDS.backgroundNoise);
      };
    }, [isRecording, isHighNoise]);

    // WaveSurfer refs for audio visualization
    const wavesurferRef = useRef<WaveSurfer | null>(null);
    const recordPluginRef = useRef<RecordPlugin | null>(null);

    /**
     * Initialize WaveSurfer for waveform visualization
     */
    const initializeWaveSurfer = useCallback(async () => {
      if (wavesurferRef.current) {
        logger.log('WaveSurfer already initialized');
        return;
      }

      logger.log('Initializing WaveSurfer...');
      try {
        const wavesurfer = WaveSurfer.create({
          container: '#waveform',
          waveColor,
          progressColor: waveProgressColor,
          cursorColor: waveProgressColor,
          barWidth: 2,
          barRadius: 2,
          barGap: 3,
          height: 16,
          normalize: true,
          backend: 'WebAudio',
        });
        wavesurferRef.current = wavesurfer;

        // Initialize record plugin
        const recordPlugin = wavesurfer.registerPlugin(
          RecordPlugin.create({
            scrollingWaveform: true,
            scrollingWaveformWindow: 8,
            renderRecordedAudio: false,
            audioBitsPerSecond: 128000,
          })
        );
        recordPluginRef.current = recordPlugin;

        // Start recording with the microphone
        await recordPlugin.startMic();
        logger.log('WaveSurfer initialized and microphone started');
      } catch (error) {
        logger.error('Failed to initialize WaveSurfer:', error);
        // Continue without visualization if WaveSurfer fails
      }
    }, [waveColor, waveProgressColor]);

    /**
     * Start recording - Connect to LiveKit and start audio capture
     */
    const startRecording = useCallback(async () => {
      logger.log('Starting recording with LiveKit...');
      setHasRecording(false);

      try {
        // Connect to LiveKit if not already connected
        if (!livekit.isConnected) {
          setIsConnectingToAgent(true);
          logger.log('Connecting to LiveKit room...');

          // Set up the promise BEFORE connecting
          const agentReadyPromise = new Promise<void>((resolve, reject) => {
            const timeout = setTimeout(() => {
              agentReadyResolverRef.current = null; // Clean up
              reject(new Error('Agent ready timeout - no session_started received'));
            }, 60000); // 60 second timeout

            // Store the resolve function so onReady can call it
            agentReadyResolverRef.current = () => {
              clearTimeout(timeout);
              resolve();
            };
          });

          await livekit.connect();

          // Wait for agent to be ready (session_started acknowledgement)
          logger.log('Waiting for agent to be ready...');
          logger.log('isAgentReady immediately after connect:', livekit.isAgentReady);

          if (!livekit.isAgentReady) {
            // Wait for the promise
            await agentReadyPromise;
          } else {
            // Already ready, clean up the resolver
            agentReadyResolverRef.current = null;
            logger.log('Agent was already ready!');
          }

          logger.log('Agent is ready!');
          setIsConnectingToAgent(false);
        }

        // Initialize WaveSurfer for visualization
        await initializeWaveSurfer();

        // Start recording
        await livekit.startRecording();

        setIsRecording(true);
        setIsPaused(false);
        setHasRecording(true);
        onRecordingStateChange?.(true, false);

        logger.log('LiveKit recording started successfully');
        toast.success('Recording started', { duration: 2000 });
      } catch (error) {
        logger.error('Failed to start LiveKit recording:', error);
        setIsConnectingToAgent(false);

        // Check if this is a backend unavailable error
        const isBackendError =
          error instanceof Error &&
          (error.message.includes('Failed to fetch') ||
            error.message.includes('backend not available') ||
            error.message.includes('Unable to connect'));

        if (isBackendError) {
          logger.error('LiveKit backend not available:', error);
          toast.error('Server unavailable. Check connection.', { duration: 3000 });
        } else if (error instanceof Error && error.name === 'NotAllowedError') {
          toast.error('Microphone access denied', { duration: 3000 });
        } else if (error instanceof Error && error.name === 'NotFoundError') {
          toast.error('No microphone found', { duration: 3000 });
        } else {
          toast.error('Failed to start recording', { duration: 3000 });
        }
      }
    }, [onRecordingStateChange, initializeWaveSurfer, livekit]);

    /**
     * Stop recording
     */
    const stopRecording = useCallback(() => {
      logger.log('Stopping LiveKit recording...');
      livekit.stopRecording();

      // Stop WaveSurfer recording but keep the waveform visible
      if (recordPluginRef.current) {
        logger.log('Stopping WaveSurfer record plugin...');
        try {
          recordPluginRef.current.stopMic();
        } catch (error) {
          logger.error('Error stopping WaveSurfer record plugin:', error);
        }
      }

      setIsRecording(false);
      setIsPaused(false);
      setHasRecording(true);
      onRecordingStateChange?.(false, false);
      toast.success('Ready to submit', { duration: 2000 });
      logger.log('LiveKit recording stopped');
    }, [onRecordingStateChange, livekit]);

    // Track if auto-start has been attempted to prevent multiple attempts
    const hasAutoStartedRef = useRef(false);

    /**
     * Auto-start recording when autoStart prop is true
     * This eliminates the need to click the "Start" button
     * Starts immediately - button shows "Connecting..." from the start
     */
    useEffect(() => {
      if (autoStart && !disabled && !isRecording && !hasRecording && !hasAutoStartedRef.current) {
        hasAutoStartedRef.current = true;
        logger.log('Auto-starting recording immediately...');
        // Start immediately - no delay needed, button already shows "Connecting..."
        startRecording();
      }
    }, [autoStart, disabled, isRecording, hasRecording, startRecording]);

    /**
     * Cleanup WaveSurfer resources
     * Extracted to avoid code duplication in success and error handlers
     */
    const cleanupWaveSurfer = useCallback(() => {
      if (recordPluginRef.current) {
        recordPluginRef.current = null;
      }
      if (wavesurferRef.current) {
        logger.log('Destroying WaveSurfer instance...');
        try {
          wavesurferRef.current.destroy();
        } catch (error) {
          logger.error('Error destroying WaveSurfer:', error);
        }
        wavesurferRef.current = null;
      }
    }, []);

    /**
     * Shared submission logic for both Submit and Save Draft.
     * Signals the LiveKit agent to process audio and generate a summary;
     * onSessionSummary routes the result based on isDraftSubmissionRef.
     */
    const beginSubmission = useCallback(
      async (mode: 'submit' | 'draft') => {
        logger.log('beginSubmission called', { mode });

        // Prevent duplicate submissions
        if (hasSubmitted) {
          logger.warn('Already submitted, ignoring duplicate submission');
          return;
        }

        // Save consultation record immediately on click - submit-mode only
        if (mode === 'submit' && onSaveRecord) {
          onSaveRecord();
        }

        isDraftSubmissionRef.current = mode === 'draft';

        // Mark as submitted immediately to prevent re-submissions and disable resume
        setHasSubmitted(true);

        logger.log('Submitting LiveKit recording for analysis...');
        try {
          setIsSubmitting(true);

          // Stop recording if still active
          if (livekit.isRecording) {
            logger.log('Stopping LiveKit recording before submit...');
            await livekit.stopRecording();
            await new Promise((resolve) => setTimeout(resolve, 500));
          }

          // Send control message to trigger analysis
          await livekit.submitForAnalysis();

          // Guard: prevent state updates if component unmounted during async operations
          if (!isMountedRef.current) return;

          logger.log('LiveKit analysis submission successful');
          toast.success('Processing consultation...', { duration: 2000 });

          // Stage 1: At 20s show a "please wait" warning (no retry yet)
          // Note: These timeouts are cleared when summary arrives, so if they fire, we're still waiting
          waitWarningTimeoutRef.current = setTimeout(() => {
            logger.log('Summary taking longer than expected - showing wait warning');
            toast.warning('Summary generation is taking longer than expected. Please wait...', {
              duration: 5000,
            });
            waitWarningTimeoutRef.current = null;
          }, 20000);

          // Stage 2: At 45s enable the retry button
          summaryTimeoutRef.current = setTimeout(() => {
            logger.error('Summary timeout - no response after 45s, enabling retry');
            setIsSubmitting(false);
            setHasSubmitted(false);
            isDraftSubmissionRef.current = false;
            livekit.resetForRetry(); // Reset LiveKit hook state for retry
            toast.error('Taking longer than expected. Please try again.', { duration: 3000 });
            summaryTimeoutRef.current = null;
          }, 45000);
        } catch (error) {
          // Guard: prevent state updates if component unmounted during async operations
          if (!isMountedRef.current) return;

          // Reset state to allow retry on any error
          const errorMessage = error instanceof Error ? error.message : String(error);
          logger.error('Failed to submit LiveKit analysis:', error);

          if (
            !errorMessage.includes('PC manager is closed') &&
            !errorMessage.includes('not connected')
          ) {
            // Actual error - show immediately
            toast.error('Failed to submit. Please try again.', { duration: 3000 });
            setIsSubmitting(false);
            setHasSubmitted(false); // Allow retry
            isDraftSubmissionRef.current = false;
            livekit.resetForRetry(); // Reset LiveKit hook state for retry
          } else {
            // Connection closed during submit - wait a bit then show error
            // This gives time for the backend to potentially still process
            logger.log('Connection closed during submit - waiting for potential backend response');
            setTimeout(() => {
              // Guard: prevent state updates if component unmounted during timeout
              if (!isMountedRef.current) return;

              logger.error('No response after connection closed - allowing retry');
              setIsSubmitting(false);
              setHasSubmitted(false); // Allow retry
              isDraftSubmissionRef.current = false;
              livekit.resetForRetry(); // Reset LiveKit hook state for retry
              toast.error('Connection lost. Please try again.', { duration: 3000 });
            }, 10000); // 10 second timeout when connection is already closed
          }
        }
      },
      [stopRecording, cleanupWaveSurfer, livekit, hasSubmitted, onSaveRecord]
    );

    const submitForAnalysis = useCallback(() => beginSubmission('submit'), [beginSubmission]);
    const saveDraftInternal = useCallback(() => beginSubmission('draft'), [beginSubmission]);

    useImperativeHandle(
      ref,
      () => ({ saveDraft: saveDraftInternal, getDurationSeconds: () => duration }),
      [saveDraftInternal, duration]
    );

    /**
     * Quick Submit — ends the consultation and returns the doctor to the
     * dashboard immediately, without waiting for the AI summary. Unlike
     * beginSubmission, there is no isSubmitting spinner and no 20s/45s
     * timeouts: session_quick_end never sends anything back on this data
     * channel, so there is nothing here to wait for. The summary is written
     * directly to Medplum by the agent in the background instead.
     */
    const quickSubmit = useCallback(async () => {
      if (hasSubmitted) {
        logger.warn('Already submitted, ignoring duplicate quick submit');
        return;
      }

      setHasSubmitted(true);

      // Save consultation record immediately on click, same as normal Submit —
      // marked as Quick Submit so the dashboard can tell it apart later.
      // Guarded so a failure here can never stop navigation below.
      try {
        onSaveRecord?.(true);
      } catch (error) {
        logger.error('onSaveRecord threw during quick submit:', error);
      }

      // Stopping the recording is a fast, local operation, so it's fine to
      // wait briefly for it — but never let it block navigation either.
      if (livekit.isRecording) {
        try {
          logger.log('Stopping LiveKit recording before quick submit...');
          await livekit.stopRecording();
        } catch (error) {
          logger.warn('Failed to stop recording before quick submit:', error);
        }
      }

      // Wait for the quick-end signal to actually finish sending before
      // navigating away. Navigating unmounts this page, which disconnects the
      // LiveKit room — if that happens before the send completes, the message
      // can be cut off mid-flight and the agent never receives it at all,
      // silently losing the whole submission. Sending is normally near-instant
      // (we're not waiting on any reply, just confirmation the bytes went
      // out), so this doesn't reintroduce the original "stuck on this page"
      // bug — the 3s cap below is just a safety net in case the connection is
      // genuinely stuck, so a bad network still can't strand the doctor here.
      await Promise.race([
        livekit.quickSubmit().catch((error) => {
          logger.error('Failed to send quick-end signal:', error);
        }),
        new Promise((resolve) => setTimeout(resolve, 3000)),
      ]);

      onQuickSubmit?.();
    }, [hasSubmitted, onSaveRecord, livekit, onQuickSubmit]);

    /**
     * Pause recording - mutes the LiveKit audio track so no audio is sent to backend
     */
    const pauseRecording = async () => {
      logger.log('Pausing LiveKit recording...');

      // This will mute the audio track - no audio sent to backend during pause
      await livekit.pauseRecording();

      // Pause the waveform recording by stopping the microphone visualization
      if (recordPluginRef.current) {
        try {
          logger.log('Attempting to pause WaveSurfer recording...');
          recordPluginRef.current.pauseRecording();
          recordPluginRef.current.stopMic();
          logger.log('WaveSurfer recording and microphone paused successfully');
        } catch (error) {
          logger.error('Failed to pause WaveSurfer recording:', error);
        }
      }

      setIsPaused(true);
      setHasRecording(true);
      onRecordingStateChange?.(true, true);
      // Toast is shown by the hook, no need to duplicate
    };

    /**
     * Resume recording - unmutes the LiveKit audio track to resume sending audio
     */
    const resumeRecording = async () => {
      logger.log('Resuming LiveKit recording...');

      // This will unmute the audio track - audio resumes being sent to backend
      await livekit.resumeRecording();

      // Resume the waveform recording by restarting microphone visualization
      if (recordPluginRef.current) {
        try {
          logger.log('Attempting to resume WaveSurfer recording...');
          await recordPluginRef.current.startMic();
          recordPluginRef.current.resumeRecording();
          logger.log('WaveSurfer recording and microphone resumed successfully');
        } catch (error) {
          logger.error('Failed to resume WaveSurfer recording:', error);
        }
      }

      setIsPaused(false);
      onRecordingStateChange?.(true, false);
      // Toast is shown by the hook, no need to duplicate;
      toast.info('Recording resumed', { duration: 2000 });
    };

    /**
     * Cleanup on unmount ONLY
     */
    useEffect(() => {
      return () => {
        logger.log('Cleanup effect running - component unmounting');

        // Clean up WaveSurfer
        if (recordPluginRef.current) {
          logger.log('Cleaning up WaveSurfer record plugin on unmount');
          try {
            recordPluginRef.current.stopMic();
          } catch (error) {
            logger.error('Error stopping WaveSurfer record plugin on unmount:', error);
          }
          recordPluginRef.current = null;
        }
        if (wavesurferRef.current) {
          logger.log('Cleaning up WaveSurfer instance on unmount');
          try {
            wavesurferRef.current.destroy();
          } catch (error) {
            logger.error('Error destroying WaveSurfer on unmount:', error);
          }
          wavesurferRef.current = null;
        }

        // Clean up all timeouts to prevent stale timeout errors
        if (summaryTimeoutRef.current) {
          logger.log('Cleaning up summary timeout on unmount');
          clearTimeout(summaryTimeoutRef.current);
          summaryTimeoutRef.current = null;
        }
        if (waitWarningTimeoutRef.current) {
          clearTimeout(waitWarningTimeoutRef.current);
          waitWarningTimeoutRef.current = null;
        }

        // Reset LiveKit state on unmount to ensure clean state
        // Safe to call regardless of submission status
        logger.log('Resetting LiveKit state on unmount');
        livekit.resetForRetry();
        isDraftSubmissionRef.current = false;
      };
    }, []);

    /**
     * Get dynamic button text based on processing status
     */
    const getSubmitButtonText = (): string => {
      if (hasSubmitted && !isSubmitting) {
        return 'Submitted';
      }

      if (!isSubmitting) {
        return 'Save record';
      }

      // Show processing status if available from backend
      const status = livekit.processingStatus;
      if (status) {
        switch (status) {
          case 'transcribing':
            return 'Transcribing...';
          case 'analyzing':
            return 'Analyzing...';
          case 'generating_summary':
            return 'Generating Summary...';
          case 'complete':
            return 'Almost done...';
          case 'error':
            return 'Error - Retrying...';
          default:
            return 'Processing...';
        }
      }

      // Default submitting text when no status from backend yet
      return 'Saving...';
    };

    const getSubmitButtonLabel = (): string => {
      if (hasSubmitted && !isSubmitting) return 'Submitted';
      if (!isSubmitting) return 'Save record';
      return getSubmitButtonText();
    };

    // Quick Submit strip: revealed on hover/focus of the Submit button (and
    // stays open while the pointer is over the strip itself), or automatically
    // once recording passes 30s — matches the reference design's tablet behavior
    // without needing to detect touch/tablet explicitly.
    const [isQuickSubmitRevealed, setIsQuickSubmitRevealed] = useState(false);
    const canQuickSubmit =
      (hasRecording || isRecording) &&
      !hasSubmitted &&
      !isSubmitting &&
      !(isRecording && !isPaused);
    const showQuickSubmit = canQuickSubmit && (isQuickSubmitRevealed || duration >= 30);

    // The strip sits above the button with a small visual gap, so the pointer
    // briefly leaves both elements while moving from one to the other. Hiding
    // immediately on mouse-leave would close the strip before it can be
    // reached, so hiding is delayed slightly and cancelled if the pointer (or
    // focus) re-enters either element in time.
    const quickSubmitHideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const revealQuickSubmit = () => {
      if (quickSubmitHideTimeoutRef.current) {
        clearTimeout(quickSubmitHideTimeoutRef.current);
        quickSubmitHideTimeoutRef.current = null;
      }
      setIsQuickSubmitRevealed(true);
    };
    const hideQuickSubmit = () => {
      if (quickSubmitHideTimeoutRef.current) {
        clearTimeout(quickSubmitHideTimeoutRef.current);
      }
      quickSubmitHideTimeoutRef.current = setTimeout(() => {
        setIsQuickSubmitRevealed(false);
        quickSubmitHideTimeoutRef.current = null;
      }, 350);
    };
    useEffect(() => {
      return () => {
        if (quickSubmitHideTimeoutRef.current) {
          clearTimeout(quickSubmitHideTimeoutRef.current);
        }
      };
    }, []);

    return (
      <div className="relative w-full">
        {/* Quick Submit strip — appears above the action bar */}
        {showQuickSubmit && (
          <div
            className="absolute bottom-full left-0 right-0 z-10 mb-2 flex items-center justify-end"
            onMouseEnter={revealQuickSubmit}
            onMouseLeave={hideQuickSubmit}
          >
            <div className="flex items-center gap-2.5 rounded-xl border border-[#eccd85] bg-[#fffdf7] px-3 py-2.5 shadow-lg">
              <span className="flex size-6 shrink-0 items-center justify-center rounded-md bg-[#f8ead0] text-xs text-[#8a6512]">
                ⚡
              </span>
              <div className="max-w-[280px] font-hanken text-[11.5px] leading-[1.5] text-[#6d4f0c]">
                In a hurry? <b className="font-medium">Quick Submit</b> sends the consultation and
                returns you to the dashboard. The summary will be ready for you to verify there.
              </div>
              <Button
                onClick={quickSubmit}
                className="h-8 shrink-0 whitespace-nowrap rounded-lg bg-[#14245c] px-3.5 font-hanken text-xs font-medium text-white hover:bg-[#0a1a4a]"
              >
                Quick Submit
              </Button>
            </div>
          </div>
        )}
        {/* Gradient blur background matching Figma exactly, sized down to Figma's compact proportions */}
        <div className={gradientBarClass}>
          <div className="flex items-center gap-[8px]">
            {/* Left section: Mic + Waveform + Timer */}
            <div className="flex h-[28px] flex-1 items-center gap-[8px] rounded-[8px] border border-solid border-[#f8f9fb] px-[8px]">
              {/* Mic Icon */}
              <div className="flex size-[16px] shrink-0 items-center justify-center">
                <Mic
                  className="size-[16px]"
                  fill={isRecording && !isPaused ? micActiveColor : micInactiveColor}
                />
              </div>
              {/* Waveform - always render container but hide when not recording */}
              {/* WaveSurfer renders the waveform upside-down by default, so we rotate it 180 degrees to display it correctly. */}{' '}
              <div id="waveform" className={`h-[16px] flex-1 rotate-180 overflow-hidden`}></div>
              {/* Timer */}
              <div className="px-[5px] py-[2px]">
                <span className="font-poppins text-[10px] text-[#717072]">
                  {formatDuration(duration)}
                </span>
              </div>
            </div>

            {/* Right section: Action buttons */}
            <div className="flex h-[28px] items-center gap-[5px]">
              {/* Start Button */}
              {!isRecording && !hasRecording && (
                <Button
                  onClick={startRecording}
                  disabled={disabled || isConnectingToAgent}
                  loading={isConnectingToAgent}
                  className={`h-7 gap-1 rounded-lg px-3 font-hanken text-xs font-medium leading-4 tracking-[-0.16px] text-[#f8f9fb] ${accentBgClass} ${accentHoverBgClass}`}
                  leftIcon={
                    isConnectingToAgent ? undefined : (
                      <Play className="h-[14px] w-[14px] text-[#f8f9fb]" />
                    )
                  }
                >
                  {isConnectingToAgent ? 'Connecting...' : 'Start'}
                </Button>
              )}

              {/* Save Draft Button - saves progress and returns to dashboard. */}
              {(isRecording || hasRecording) && !hasSubmitted && onSaveDraft && (
                <Button
                  onClick={saveDraftInternal}
                  disabled={isSavingDraft || isSubmitting}
                  loading={isSavingDraft}
                  title="Save draft - saves progress and returns to dashboard"
                  className="h-7 gap-1 rounded-lg border border-[#B5BBD3] bg-[#FFFFFF] px-3 font-hanken text-xs font-medium leading-4 tracking-[-0.16px] text-[#061A61] hover:bg-[#F8F9FB]"
                  leftIcon={
                    !isSavingDraft ? <SaveDraft className="h-[14px] w-[14px]" /> : undefined
                  }
                >
                  Save Draft
                </Button>
              )}

              {/* On Hold Button — mutes audio, consultation stays open to resume. */}
              {isRecording && !isPaused && !hasSubmitted && (
                <Button
                  onClick={pauseRecording}
                  title="Put on hold — audio stops, resume when ready"
                  className={`h-7 gap-1 rounded-lg border border-[#BCC0D7] bg-white px-3 font-hanken text-xs font-medium leading-4 tracking-[-0.16px] hover:bg-[#F8F9FB] ${accentTextClass}`}
                  leftIcon={<OnHoldIcon color={accentColor} />}
                >
                  On Hold
                </Button>
              )}

              {/* Resume Button - show when paused and not yet submitted */}
              {isPaused && !hasSubmitted && (
                <Button
                  onClick={resumeRecording}
                  className="h-7 gap-1 rounded-lg bg-[#f8f9fb] px-3 font-hanken text-xs font-medium leading-4 tracking-[-0.16px] text-[#526EC5] hover:bg-[#e8e9eb]"
                  leftIcon={<RotateCcw className="h-[14px] w-[14px]" />}
                >
                  Resume
                </Button>
              )}

              {/* Save record Button - enabled when paused or has recording, disabled when actively recording or already submitted */}
              {(hasRecording || isRecording) && (
                <Button
                  onClick={submitForAnalysis}
                  onMouseEnter={canQuickSubmit ? revealQuickSubmit : undefined}
                  onMouseLeave={canQuickSubmit ? hideQuickSubmit : undefined}
                  onFocus={canQuickSubmit ? revealQuickSubmit : undefined}
                  onBlur={canQuickSubmit ? hideQuickSubmit : undefined}
                  disabled={isSubmitting || hasSubmitted || (isRecording && !isPaused)}
                  loading={isSubmitting}
                  className={`h-7 gap-1 rounded-lg px-3 font-hanken text-xs font-medium leading-4 tracking-[-0.16px] ${
                    (isRecording && !isPaused) || hasSubmitted
                      ? 'cursor-not-allowed border border-[#f8f9fb] bg-[#e2e1e4] text-[#8d90a1]'
                      : `text-[#f8f9fb] ${accentBgClass} ${accentHoverBgClass}`
                  }`}
                  leftIcon={!isSubmitting ? <SaveRecordIcon /> : undefined}
                >
                  {getSubmitButtonLabel()}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }
);

AudioRecorder.displayName = 'AudioRecorder';
